import * as React from "react"
import { useState, useEffect } from "react"
import { useDebounce } from "../../utils/hooks"
import { DropdownMenu } from "./dropdownMenu"
import { City } from "../../business_logic/model"
import { googlePlacesAPIKey } from "../../business_logic/constants"

let autoComplete

const loadScript = (url: string, callback: () => void) => {
  let script = document.createElement("script")
  script.type = "text/javascript"
  script.onload = callback
  script.src = url
  document.getElementsByTagName("head")[0].appendChild(script)
}

interface CityDropdownProps extends React.HTMLAttributes<HTMLDivElement> {
  value: City
  setValue: React.Dispatch<City>
}

export const CityDropdown = ({ value, setValue, ...others }: CityDropdownProps): JSX.Element => {
  /*
  Dropdown looking for cities based on the teleport api
  * value: currently used city value
  * setValue: sets the value
  */

  // Input search
  const [searchValue, setSearchValue] = useState<string>(
    value?.matching_full_name?.split("(")[0].trim(),
  )
  // List of options, updated based on the input
  const [options, setOptions] = useState([])
  // Currently highlighted options
  const [activeOption, setActiveOption] = useState(0)

  // Sets the city value and updates the search input to match
  const setAll = option => {
    setSearchValue(option.matching_full_name.split("(")[0].trim())
    if (setValue) {
      setValue(options[activeOption])
    }
  }

  // Debounce the search to avoid overloading the teleport api
  const debouncedSearchValue = useDebounce(searchValue, 300)

  // Resets the active option to the first one when the search value is updated
  useEffect(() => {
    if (searchValue) {
      setActiveOption(0)
    }
  }, [searchValue])

  // // Fetch cities matching the input and update the options
  // useEffect(() => {
  //   async function fetchData() {
  //     const response = await fetch(
  //       `https://api.teleport.org/api/cities/?search=${debouncedSearchValue}&limit=5`
  //     )
  //     const responseData = await response.json()
  //     setOptions(responseData._embedded["city:search-results"])
  //   }
  //   if (debouncedSearchValue !== "") {
  //     fetchData()
  //   }
  // }, [debouncedSearchValue])

  useEffect(() => {
    if (!window.google?.maps) {
      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=${googlePlacesAPIKey}&libraries=places`,
        () => {
          autoComplete = new window.google.maps.places.AutocompleteService()
        },
      )
    }
  }, [])

  useEffect(() => {
    if (autoComplete && debouncedSearchValue !== "") {
      autoComplete.getPlacePredictions(
        { input: debouncedSearchValue, types: ["(cities)"] },
        async responseData => {
          setOptions(
            responseData.map(c => ({ matching_full_name: c.description, place_id: c.place_id })),
          )
        },
      )
    }
  }, [debouncedSearchValue])

  return (
    <div {...others}>
      <div className="relative mt-1">
        {/* Search input */}
        <input
          className="input relative peer"
          type="text"
          value={searchValue}
          onChange={e => setSearchValue(e.target.value)}
          onKeyPress={e => {
            e.key === "Enter" && e.preventDefault()
          }}
          placeholder="Search a city"
        />
        {/* Dropdown menu */}
        <DropdownMenu
          options={options}
          activeOption={activeOption}
          setActiveOption={setActiveOption}
          setAll={setAll}
          renderOption={o => o.matching_full_name.split("(")[0].trim()}
          isOptionSelected={o => searchValue === o.matching_full_name.split("(")[0].trim()}
        />
      </div>
    </div>
  )
}
